import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { MapPin, Pencil, Plus } from "lucide-react";

import { transportService, type Route } from "../../services/transportService";
import { apiErrorMessage } from "../../utils/apiError";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { errorLine } from "./TransportParts";
import { ROUTE_TYPES, clock, labelFrom, money, nextStopNumber, routeConfig, routeStatus } from "./transportLogic";

function RouteForm({ route, onClose, onDone }: { route?: Route; onClose: () => void; onDone: (route: Route) => void }) {
  const [taking, setTaking] = useState(route?.is_available ?? true);
  const [running, setRunning] = useState(route?.is_active ?? true);
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => (route ? transportService.updateRoute({ id: route.id, data }) : transportService.createRoute(data)),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    save.mutate({
      name: text("name"),
      route_type: text("route_type"),
      description: text("description") || null,
      start_location: text("start_location"),
      end_location: text("end_location"),
      distance_km: text("distance_km") || null,
      estimated_duration_minutes: text("duration") ? Number(text("duration")) : null,
      departure_time: text("departure_time"),
      arrival_time: text("arrival_time"),
      monthly_fee: text("monthly_fee"),
      term_fee: text("term_fee") || null,
      annual_fee: text("annual_fee") || null,
      max_students: Number(text("max_students")),
      is_available: taking,
      is_active: running,
      notes: text("notes") || null,
    });
  };
  const value = (name: keyof Route) => (route?.[name] as string | number | null | undefined) ?? "";

  return (
    <FormModal
      id="route"
      title={route ? `Edit ${route.name}` : "New route"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The route could not be saved.") : undefined}
      saveLabel={route ? "Save changes" : "Create the route"}
      note={route ? undefined : "Add its stops once it is saved."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The route" divided={false}>
        <Field label="Name" hint="e.g. Kira – Namugongo.">
          <input required name="name" maxLength={100} defaultValue={value("name")} className="input mt-1" />
        </Field>
        <Field label="Runs">
          <select name="route_type" defaultValue={value("route_type") || "both"} className="input mt-1">
            {ROUTE_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Starts at">
          <input required name="start_location" maxLength={200} defaultValue={value("start_location")} className="input mt-1" />
        </Field>
        <Field label="Ends at">
          <input required name="end_location" maxLength={200} defaultValue={value("end_location")} className="input mt-1" />
        </Field>
        <Field label="Description" optional className="sm:col-span-2" hint="The areas it covers, for families choosing a route.">
          <textarea name="description" rows={2} defaultValue={value("description")} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Timing and distance" columns={4}>
        <Field label="Leaves">
          <input required type="time" name="departure_time" defaultValue={clock(route?.departure_time) || "06:30"} className="input mt-1" />
        </Field>
        <Field label="Arrives">
          <input required type="time" name="arrival_time" defaultValue={clock(route?.arrival_time) || "07:30"} className="input mt-1" />
        </Field>
        <Field label="Distance (km)" optional>
          <input type="number" name="distance_km" min={0} step="0.1" defaultValue={value("distance_km")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Minutes" optional>
          <input type="number" name="duration" min={0} step={1} defaultValue={value("estimated_duration_minutes")} className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>

      <FormSection title="Fees and places" columns={4}>
        <Field label="Per month (UGX)">
          <input required type="number" name="monthly_fee" min={0} step="any" defaultValue={value("monthly_fee")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Per term" optional hint="Else 3 months.">
          <input type="number" name="term_fee" min={0} step="any" defaultValue={value("term_fee")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Per year" optional hint="Else 12 months.">
          <input type="number" name="annual_fee" min={0} step="any" defaultValue={value("annual_fee")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Places">
          <input required type="number" name="max_students" min={Math.max(1, route?.current_students ?? 1)} step={1} defaultValue={value("max_students") || 30} className="input mt-1 tabular-nums" />
        </Field>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 sm:col-span-2">
          <input type="checkbox" checked={taking} onChange={(event) => setTaking(event.target.checked)} className="h-4 w-4 rounded border-gray-300" />
          Taking new riders
        </label>
        {route && (
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 sm:col-span-2">
            <input type="checkbox" checked={running} onChange={(event) => setRunning(event.target.checked)} className="h-4 w-4 rounded border-gray-300" />
            The route is running
          </label>
        )}
        <Field label="Notes" optional className="sm:col-span-2 lg:col-span-4">
          <textarea name="notes" rows={2} defaultValue={value("notes")} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Where the buses go, their stops and times, and how many places are left. */
export function RoutesPanel({ routes, isLoading, isError, onRetry, canManage, onChanged , initialView}: {
  /** A register view to open on, e.g. "papers" from the dashboard. */
  initialView?: string;
  routes?: Route[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<Route | "new" | null>(null);
  const [stopForm, setStopForm] = useState(0);
  // The route list refreshes after the form remounts, so remember the stop
  // just added to number the next one.
  const [lastStop, setLastStop] = useState<{ route: number; number: number } | null>(null);
  const addStop = useMutation({
    mutationFn: transportService.createStop,
    onSuccess: (stop) => {
      setLastStop({ route: stop.route, number: stop.sequence_order });
      setStopForm((count) => count + 1);
      setNotice({ tone: "success", text: `Stop ${stop.sequence_order}, ${stop.name}, added.` });
      onChanged();
    },
  });
  const toggleStop = useMutation({ mutationFn: transportService.updateStop, onSuccess: () => onChanged() });

  const columns: RegisterColumn<Route>[] = [
    { header: "Route", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[10rem]" },
    { header: "From → to", cell: (row) => `${row.start_location} → ${row.end_location}`, exportValue: (row) => `${row.start_location} to ${row.end_location}`, className: "min-w-[12rem] whitespace-normal text-gray-700" },
    { header: "Runs", cell: (row) => labelFrom(ROUTE_TYPES, row.route_type), exportValue: (row) => labelFrom(ROUTE_TYPES, row.route_type), className: "text-gray-700" },
    { header: "Times", cell: (row) => `${clock(row.departure_time)}–${clock(row.arrival_time)}`, exportValue: (row) => `${clock(row.departure_time)}-${clock(row.arrival_time)}`, className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Stops", cell: (row) => row.stops.filter((stop) => stop.is_active).length, exportValue: (row) => row.stops.filter((stop) => stop.is_active).length, align: "right" },
    { header: "Riders", cell: (row) => `${row.current_students} / ${row.max_students}`, exportValue: (row) => row.current_students, align: "right" },
    { header: "Per month", cell: (row) => money(row.monthly_fee), exportValue: (row) => Number(row.monthly_fee), align: "right" },
    { header: "Status", cell: (row) => <StatusDot {...routeStatus(row)} />, exportValue: (row) => routeStatus(row).label },
  ];

  const route = viewing !== null ? routes?.find((row) => row.id === viewing) : undefined;
  const stops = [...(route?.stops ?? [])].sort((a, b) => a.sequence_order - b.sequence_order);
  const nextNumber = Math.max(nextStopNumber(stops), lastStop && route && lastStop.route === route.id ? lastStop.number + 1 : 0);

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title="Routes"
        description={canManage ? "Open a route to see its stops, add stops or edit it." : "Where the buses go and when."}
        actions={canManage && (
          <button type="button" onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New route
          </button>
        )}
        rows={routes}
        config={routeConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          addStop.reset();
          setViewing(row.id);
        }}
        noun={["route", "routes"]}
        exportName={{ file: "transport-routes", title: "Transport Routes" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No routes yet. Create one, then add its stops." : "No routes to show."}
      />

      {route && !editing && (
        <DetailsModal
          size="3xl"
          title={route.name}
          headline={{ label: "Leaves", value: `${clock(route.departure_time)} → ${clock(route.arrival_time)}`, note: `${route.start_location} → ${route.end_location}` }}
          status={routeStatus(route)}
          facts={[
            ["Runs", labelFrom(ROUTE_TYPES, route.route_type)],
            ["Riders", `${route.current_students} of ${route.max_students}`],
            ["Distance", route.distance_km ? `${Number(route.distance_km)} km` : undefined],
            ["Takes", route.estimated_duration_minutes ? `${route.estimated_duration_minutes} min` : undefined],
            ["Per month", money(route.monthly_fee)],
            ["Per term", route.term_fee ? money(route.term_fee) : `${money(Number(route.monthly_fee) * 3)} (3 months)`],
            ["Per year", route.annual_fee ? money(route.annual_fee) : `${money(Number(route.monthly_fee) * 12)} (12 months)`],
          ]}
          sections={[
            { title: "Areas covered", body: route.description },
            { title: "Notes", body: route.notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(route)}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        >
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Stops, in order</h4>
            {stops.length ? (
              <div className="sheet-wrap mt-1.5">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th className="sheet-rownum">#</th>
                      <th>Stop</th>
                      <th>Where</th>
                      <th>Bus there</th>
                      <th>Leaves</th>
                      <th>Used for</th>
                      {canManage && <th className="text-right">In use</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {stops.map((stop) => (
                      <tr key={stop.id} className={stop.is_active ? undefined : "opacity-50"}>
                        <td className="sheet-rownum">{stop.sequence_order}</td>
                        <td className="font-medium text-gray-900">{stop.name}</td>
                        <td className="min-w-[10rem] whitespace-normal text-gray-700">{stop.address}</td>
                        <td className="tabular-nums">{clock(stop.arrival_time)}</td>
                        <td className="tabular-nums">{clock(stop.departure_time)}</td>
                        <td className="text-gray-700">{[stop.is_pickup_point && "Pickup", stop.is_dropoff_point && "Drop-off"].filter(Boolean).join(", ") || "—"}</td>
                        {canManage && (
                          <td className="text-right">
                            <button
                              type="button"
                              disabled={toggleStop.isPending}
                              onClick={() => toggleStop.mutate({ id: stop.id, data: { is_active: !stop.is_active } })}
                              className="rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                            >
                              {stop.is_active ? "Stop using" : "Use again"}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-1.5 text-sm text-gray-500">No stops yet{canManage ? "; add the first below" : ""}.</p>
            )}
            {toggleStop.isError && <div className="mt-2">{errorLine(toggleStop.error, "The stop could not be changed.")}</div>}
          </section>

          {canManage && (
            <ActionPanel title="Add a stop" description="Stops are numbered in the order the bus reaches them.">
              <form
                key={stopForm}
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  const data = new FormData(event.currentTarget);
                  addStop.mutate({
                    route: route.id,
                    name: String(data.get("name")).trim(),
                    address: String(data.get("address")).trim(),
                    sequence_order: Number(data.get("sequence")),
                    arrival_time: String(data.get("arrival")),
                    departure_time: String(data.get("departure")),
                    is_pickup_point: data.get("pickup") === "on",
                    is_dropoff_point: data.get("dropoff") === "on",
                    is_active: true,
                  });
                }}
                className="grid gap-3 sm:grid-cols-4"
              >
                <Field label="Number">
                  <input required type="number" name="sequence" min={1} defaultValue={nextNumber} className="input mt-1 tabular-nums" />
                </Field>
                <Field label="Stop name" className="sm:col-span-3">
                  <input required name="name" maxLength={100} className="input mt-1" placeholder="e.g. Kira Town stage" />
                </Field>
                <Field label="Where exactly" className="sm:col-span-2">
                  <input required name="address" className="input mt-1" placeholder="e.g. opposite Shell, Kira Road" />
                </Field>
                <Field label="Bus there">
                  <input required type="time" name="arrival" className="input mt-1" />
                </Field>
                <Field label="Leaves">
                  <input required type="time" name="departure" className="input mt-1" />
                </Field>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="pickup" defaultChecked className="h-4 w-4 rounded border-gray-300" /> Pickup
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="dropoff" defaultChecked className="h-4 w-4 rounded border-gray-300" /> Drop-off
                </label>
                <div className="flex justify-end sm:col-span-2">
                  <button disabled={addStop.isPending} className="btn btn-primary gap-1.5">
                    <MapPin className="h-4 w-4" /> {addStop.isPending ? "Adding…" : "Add the stop"}
                  </button>
                </div>
              </form>
              {addStop.isError &&
                (apiErrorMessage(addStop.error, "").includes("unique set") ? (
                  <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">That stop number is already used on this route; give it the next free number.</p>
                ) : (
                  errorLine(addStop.error, "The stop could not be added.")
                ))}
            </ActionPanel>
          )}
        </DetailsModal>
      )}

      {editing && (
        <RouteForm
          route={editing === "new" ? undefined : editing}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `${saved.name} created. Add its stops now.` : `${saved.name} updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
